import {
  SaleStatus,
  SaleStatusArchetype,
  SaleStatusColors,
  SaleStatusLabels,
  SaleType,
  SelectableSaleStatuses,
} from "constants/sale";

import { getSaleStatusArchetype } from "lib/sale";

export interface SaleStatusOption {
  label: string;
  value: string;
  archetype: SaleStatusArchetype;
}

export interface ResolvedSaleStatus {
  archetype: SaleStatusArchetype;
  // null when unset or not in the viewer's catalogue.
  customStatus: SaleStatus | null;
  label: string;
  color: string;
}

// Resolve a sale's display status for a viewer. The single source of the
// "custom status name, else archetype label" rule, shared by the grids and badge.
export const resolveSaleStatus = (
  sale,
  role,
  saleStatuses: Record<string, SaleStatus> = {},
): ResolvedSaleStatus => {
  const archetype = getSaleStatusArchetype(
    sale,
    role,
    saleStatuses,
  ) as SaleStatusArchetype;
  const saleStatusId = sale?.sale_attributes?.saleStatusId;
  const customStatus = saleStatusId ? saleStatuses[saleStatusId] ?? null : null;
  return {
    archetype,
    customStatus,
    label: customStatus?.name ?? SaleStatusLabels[archetype],
    color: SaleStatusColors[customStatus?.archetype ?? archetype],
  };
};

// The "no custom status" sentinel: always shown; selecting it clears the status.
// Never a real row — the backend reserves the (name "Draft", archetype DRAFT) pair.
export const DRAFT_SENTINEL_OPTION: SaleStatusOption = {
  value: SaleStatus.DRAFT,
  label: SaleStatusLabels[SaleStatus.DRAFT],
  archetype: SaleStatus.DRAFT,
};

interface BuildSaleStatusOptionsArgs {
  saleStatuses: Record<string, SaleStatus>;
  saleType: SaleType | null;
  currentSaleStatusId: string | null;
}

/** Build the selector's status options, led by the Draft sentinel. */
export const buildSaleStatusOptions = ({
  saleStatuses,
  saleType,
  currentSaleStatusId,
}: BuildSaleStatusOptionsArgs): SaleStatusOption[] => {
  const options: SaleStatusOption[] = [DRAFT_SENTINEL_OPTION];

  SelectableSaleStatuses.forEach(archetype => {
    const customStatuses = Object.values(saleStatuses)
      .filter(
        customStatus =>
          customStatus.archetype === archetype &&
          (!customStatus.saleType ||
            customStatus.saleType === saleType ||
            customStatus.id === currentSaleStatusId),
      )
      .sort((a, b) => a.name.localeCompare(b.name));

    // An archetype with no custom status gets a bare option that provisions one
    // on select; DRAFT is covered by the sentinel and never auto-created.
    if (archetype !== SaleStatus.DRAFT && customStatuses.length === 0) {
      options.push({
        value: archetype,
        label: SaleStatusLabels[archetype],
        archetype,
      });
    }

    customStatuses.forEach(customStatus => {
      options.push({
        value: customStatus.id,
        label: customStatus.name,
        archetype: customStatus.archetype,
      });
    });
  });

  return options;
};

export enum SaleStatusSelectionKind {
  Clear = "clear",
  AssignCustom = "assignCustom",
  CreateArchetype = "createArchetype",
}

// Clear (Draft sentinel), assign an existing custom status, or provision a
// default for a bare archetype.
export const classifySaleStatusOption = (
  option: SaleStatusOption,
): SaleStatusSelectionKind => {
  if (option.value === SaleStatus.DRAFT) {
    return SaleStatusSelectionKind.Clear;
  }
  const isArchetype = (Object.values(SaleStatus) as string[]).includes(
    option.value,
  );
  return isArchetype
    ? SaleStatusSelectionKind.CreateArchetype
    : SaleStatusSelectionKind.AssignCustom;
};
